import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  DataTable,
  Field,
  FormRow,
  Input,
  RightDrawer,
} from '@projexn/ui';
import { type SiteVisitStatus } from '@projexn/shared';
import { api } from '../lib/api';

interface SiteVisit {
  id: string;
  tenderId: string;
  scheduledAt: string;
  address: string | null;
  notes: string | null;
  status: SiteVisitStatus;
  completedAt: string | null;
}

interface Tender {
  id: string;
  name: string;
  metadata?: Record<string, unknown>;
}

function refOf(t: Tender): string {
  const r = t.metadata?.reference;
  return typeof r === 'string' ? r : t.id.slice(-6).toUpperCase();
}

const STATUS_TONE: Record<SiteVisitStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  scheduled: 'info',
  completed: 'success',
  cancelled: 'neutral',
  no_show: 'danger',
};

export function SiteVisitsPage() {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);

  const visitsQ = useQuery({
    queryKey: ['site-visits'],
    queryFn: () => api.get<{ items: SiteVisit[] }>('/api/site-visits'),
  });
  const tendersQ = useQuery({
    queryKey: ['tenders'],
    queryFn: () => api.get<{ items: Tender[] }>('/api/tenders'),
  });

  const complete = useMutation({
    mutationFn: (id: string) => api.post(`/api/site-visits/${id}/complete`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['site-visits'] }),
  });

  const tenderRef = (id: string) => {
    const t = tendersQ.data?.items.find((x) => x.id === id);
    return t ? refOf(t) : '—';
  };

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Site visits</h1>
          <p className="text-sm text-zinc-500">Scheduled walk-throughs.</p>
        </div>
        <Button onClick={() => setCreating(true)}>Schedule visit</Button>
      </header>

      <Card className="overflow-hidden">
        <DataTable
          rows={visitsQ.data?.items ?? []}
          rowKey={(r) => r.id}
          columns={[
            { key: 'tender', header: 'Tender', render: (r) => <code className="text-xs">{tenderRef(r.tenderId)}</code> },
            { key: 'when', header: 'When', render: (r) => new Date(r.scheduledAt).toLocaleString() },
            { key: 'address', header: 'Address', render: (r) => r.address ?? '—' },
            { key: 'status', header: 'Status', render: (r) => <Badge tone={STATUS_TONE[r.status]}>{r.status}</Badge> },
            {
              key: 'actions',
              header: '',
              render: (r) =>
                r.status === 'scheduled' ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      complete.mutate(r.id);
                    }}
                  >
                    Complete
                  </Button>
                ) : null,
            },
          ]}
          empty={<p className="px-6 py-12 text-center text-sm text-zinc-500">No visits scheduled.</p>}
        />
      </Card>

      <RightDrawer open={creating} onClose={() => setCreating(false)} title="Schedule visit">
        <NewVisitForm
          tenders={tendersQ.data?.items ?? []}
          onCreated={() => {
            setCreating(false);
            qc.invalidateQueries({ queryKey: ['site-visits'] });
          }}
        />
      </RightDrawer>
    </div>
  );
}

function NewVisitForm({ tenders, onCreated }: { tenders: Tender[]; onCreated: () => void }) {
  const [form, setForm] = useState({
    tenderId: '',
    scheduledAt: '',
    address: '',
    notes: '',
  });

  const mutate = useMutation({
    mutationFn: () =>
      api.post('/api/site-visits', {
        tenderId: form.tenderId,
        scheduledAt: form.scheduledAt ? new Date(form.scheduledAt).toISOString() : '',
        address: form.address || undefined,
        notes: form.notes || undefined,
      }),
    onSuccess: onCreated,
  });

  return (
    <form onSubmit={(e) => { e.preventDefault(); mutate.mutate(); }} className="space-y-4">
      <FormRow>
        <Field label="Tender" required>
          <select
            required
            value={form.tenderId}
            onChange={(e) => setForm({ ...form, tenderId: e.target.value })}
            className="h-9 w-full rounded-md border border-zinc-300 bg-white px-3 text-sm"
          >
            <option value="">— select —</option>
            {tenders.map((t) => (
              <option key={t.id} value={t.id}>{refOf(t)} · {t.name}</option>
            ))}
          </select>
        </Field>
      </FormRow>
      <FormRow>
        <Field label="When" required>
          <Input
            type="datetime-local"
            required
            value={form.scheduledAt}
            onChange={(e) => setForm({ ...form, scheduledAt: e.target.value })}
          />
        </Field>
      </FormRow>
      <FormRow>
        <Field label="Address">
          <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
        </Field>
      </FormRow>
      <FormRow>
        <Field label="Notes">
          <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </Field>
      </FormRow>
      {mutate.isError && (
        <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          {(mutate.error as Error).message}
        </p>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <Button type="submit" loading={mutate.isPending}>Schedule</Button>
      </div>
    </form>
  );
}
